/**
 * Gemini-assisted code suggestions for the codebook editor.
 *
 * SECURITY NOTE: this calls the Gemini API directly from the browser using
 * a key the user stores in their localStorage. That key is NEVER safe to
 * publish — anyone who can read your browser storage (or your bundle if
 * the key was bundled) can use it. For a production deployment you'd
 * proxy this through a Cloud Function (see VITE_GEMINI_PROXY_URL in
 * .env.example + the bot.ts stub).
 *
 * For private playtesting with a known collaborator, the localStorage key
 * is a pragmatic shortcut — the warning is shown in the editor UI.
 */

import { GoogleGenerativeAI } from "@google/generative-ai";
import { BOARDS } from "../data/boards";
import type { FrameColor, IconRef, SentenceToken, Tense } from "./types";
import { parseIconKey } from "./types";

export interface SuggestedToken {
  iconKey: string;
  pos: FrameColor;
  plural?: boolean;
  tense?: Tense;
  negation?: boolean;
  compoundWithNext?: boolean;
}

const KEY_STORAGE = "lexikon.geminiKey";
const MODEL = "gemini-2.0-flash";

export function getStoredKey(): string {
  return localStorage.getItem(KEY_STORAGE) ?? "";
}
export function setStoredKey(key: string): void {
  if (key) localStorage.setItem(KEY_STORAGE, key);
  else localStorage.removeItem(KEY_STORAGE);
}

function buildIconCatalog(): string {
  const lines: string[] = [];
  for (const board of BOARDS) {
    lines.push(`# Board ${board.id} — ${board.theme.en}`);
    for (const cell of board.cells) {
      const gloss = cell.gloss.en;
      if (gloss) {
        lines.push(`${board.id}${cell.ref.col}${cell.ref.row} → ${gloss}${cell.dualImage ? "  (split cell: X = right image)" : ""}`);
      }
    }
  }
  return lines.join("\n");
}

const SYSTEM_INSTRUCTIONS = `You are helping author a sentence in the LEXIKON icon language game.

GRAMMAR ROLES (frame colors):
- noun (yellow)
- verb (red) — verbs accept tense: "past" | "present" | "future"
- adj (blue)
- prep (green) — prepositions or conjunctions
- adverb (orange)
- neutral (grey)

MARKERS:
- plural: true if the icon refers to multiple of the thing
- negation: true for "not" (or to select the X-marked alternative on a split cell)
- compoundWithNext: true when this icon should be read together with the next as one compound word
  (e.g. Science + Person = Scientist; Art + Person = Artist)

OUTPUT FORMAT (strict):
A JSON object with exactly two keys:
  "tokens": an ordered array of objects with keys { iconKey, pos, plural?, tense?, negation?, compoundWithNext? }
  "translation": the English sentence the icon sequence expresses (may differ slightly from the user's input if the icons don't perfectly cover it).

Output ONLY the JSON object — no markdown, no commentary. Pick the icons that best convey the target meaning using ONLY icons from the catalog. Order matters: the array is the reading order. If a target word has no good icon, omit it (the player infers grammar from frame color).`;

export async function suggestCode(opts: {
  targetSentence: string;
  apiKey: string;
}): Promise<{ tokens: SuggestedToken[]; translation: string }> {
  const ai = new GoogleGenerativeAI(opts.apiKey);
  const model = ai.getGenerativeModel({
    model: MODEL,
    systemInstruction: SYSTEM_INSTRUCTIONS,
    generationConfig: {
      responseMimeType: "application/json",
      temperature: 0.4,
    },
  });
  const prompt = `ICON CATALOG (cell ID → English meaning):\n${buildIconCatalog()}\n\nTARGET SENTENCE: "${opts.targetSentence}"\n\nReturn the JSON now.`;
  let result;
  try {
    result = await model.generateContent(prompt);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // Free-tier quota = 0 → this Google account isn't eligible for free tier.
    // Common cause: Google Workspace accounts (custom domains). Surface a
    // friendlier diagnosis so the user knows what to fix.
    if (/\b429\b/.test(msg) && /limit:\s*0/.test(msg) && /free_tier/i.test(msg)) {
      throw new Error(
        "Gemini free tier isn't available for this Google account / project " +
          "(common with Google Workspace accounts). Three fixes:\n\n" +
          "1) Sign in to aistudio.google.com with a personal @gmail.com account and create a key there\n" +
          "2) Enable billing on the GCP project (Flash is ~$0.075 per 1M tokens — cents/month for playtest use)\n" +
          "3) Ask your Workspace admin to enable 'Generative AI' services for your account",
      );
    }
    throw err;
  }
  const text = result.response.text();
  let parsed: { tokens?: unknown; translation?: unknown };
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`Gemini didn't return valid JSON:\n${text.slice(0, 200)}`);
  }
  if (!Array.isArray(parsed.tokens)) {
    throw new Error("Gemini response missing 'tokens' array.");
  }
  const tokens: SuggestedToken[] = [];
  for (const raw of parsed.tokens as unknown[]) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    if (typeof r.iconKey !== "string") continue;
    try {
      parseIconKey(r.iconKey);
    } catch {
      continue;
    }
    tokens.push({
      iconKey: r.iconKey,
      pos: (r.pos as FrameColor) ?? "noun",
      plural: Boolean(r.plural),
      tense: r.tense as Tense | undefined,
      negation: Boolean(r.negation),
      compoundWithNext: Boolean(r.compoundWithNext),
    });
  }
  return { tokens, translation: typeof parsed.translation === "string" ? parsed.translation : opts.targetSentence };
}

export function suggestedTokensToSentenceTokens(suggested: SuggestedToken[]): SentenceToken[] {
  return suggested.map((s, i): SentenceToken => {
    const icon: IconRef = parseIconKey(s.iconKey);
    return {
      icon,
      order: i + 1,
      frameColor: s.pos,
      plural: s.plural,
      tense: s.tense,
      negation: s.negation,
      compoundWithNext: s.compoundWithNext,
    };
  });
}
